import React from "react";

import {
  TLBaseShape,
  RecordProps,
  T,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
  Vec,
  Editor,
  TLResizeInfo,
} from "@tldraw/tldraw";
import { getDashArray } from "../new-geo-shape-helpers";
import { HOVER_EFFECT } from "./constants";
import { mixColors } from "./CustomCloudUtil";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { defaultScale } from "../constants";

type CustomStarUtilType = TLBaseShape<
  "customStar",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth: number;
    filterBlur?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
  }
>;

export class CustomStarUtil extends BaseBoxShapeUtil<CustomStarUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customStar" as const;
  static override props: RecordProps<CustomStarUtilType> = {
    w: T.number,
    h: T.number,
    fill: T.string,
    outlineColor: T.string,
    dash: T.any,
    transparency: T.number,
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.number,
    filterBlur: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any,
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isShowIndicator: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    scale: T.optional(T.number),
  };

  getDefaultProps(): CustomStarUtilType["props"] {
    return {
      w: 100,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      filterBlur: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getGeometry(shape: CustomStarUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }

  hideSelectionBoundsFg(_shape: CustomStarUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomStarUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomStarUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: CustomStarUtilType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: CustomStarUtilType, info: TLResizeInfo<CustomStarUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  component(shape: CustomStarUtilType) {
    const { w, h, outlineColor, dash, strokeDasharray, outShadowArray, inShadowArray } = shape.props;

    const sides = 5;
    const step = (Math.PI * 2) / sides / 2;

    const rightMostIndex = Math.floor(sides / 4) * 2;
    const leftMostIndex = sides * 2 - rightMostIndex;
    const topMostIndex = 0;
    const bottomMostIndex = Math.floor(sides / 2) * 2;

    const maxX = (Math.cos(-Math.PI / 2 + rightMostIndex * step) * w) / 2;
    const minX = (Math.cos(-Math.PI / 2 + leftMostIndex * step) * w) / 2;

    const minY = (Math.sin(-Math.PI / 2 + topMostIndex * step) * h) / 2;
    const maxY = (Math.sin(-Math.PI / 2 + bottomMostIndex * step) * h) / 2;

    const diffX = w - Math.abs(maxX - minX);
    const diffY = h - Math.abs(maxY - minY);
    const offsetX = w / 2 + minX - (w / 2 - maxX);
    const offsetY = h / 2 + minY - (h / 2 - maxY);

    const ratio = 1;
    const cx = (w - offsetX) / 2;
    const cy = (h - offsetY) / 2;
    const ox = (w + diffX) / 2;
    const oy = (h + diffY) / 2;
    const ix = (ox * ratio) / 2;
    const iy = (oy * ratio) / 2;

    const shadowFilterId = `shadow-${shape.id}`;
    const dashArray = strokeDasharray || getDashArray(dash);

    const points = Array.from({ length: sides * 2 }).map((_, i) => {
      const theta = -Math.PI / 2 + i * step;
      return new Vec(cx + (i % 2 ? ix : ox) * Math.cos(theta), cy + (i % 2 ? iy : oy) * Math.sin(theta));
    });
    const pathData = points.map((p, i) => (i === 0 ? "M" : "L") + ` ${p.x} ${p.y}`).join(" ") + " Z";

    return this.shapeUtilHelper.useRenderedShape(
      shape,
      pathData,
      outlineColor,
      dashArray,
      shadowFilterId,
      outShadowArray,
      inShadowArray,
      this.indicator.bind(this)
    );
  }

  addDefaultColorMix(shape: CustomStarUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomStarUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomStarUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: CustomStarUtilType) {
    const {
      h,
      w,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
    } = shape.props;
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const sides = 5;
    const step = (Math.PI * 2) / sides / 2;

    const rightMostIndex = Math.floor(sides / 4) * 2;
    const leftMostIndex = sides * 2 - rightMostIndex;
    const topMostIndex = 0;
    const bottomMostIndex = Math.floor(sides / 2) * 2;

    const maxX = (Math.cos(-Math.PI / 2 + rightMostIndex * step) * w) / 2;
    const minX = (Math.cos(-Math.PI / 2 + leftMostIndex * step) * w) / 2;

    const minY = (Math.sin(-Math.PI / 2 + topMostIndex * step) * h) / 2;
    const maxY = (Math.sin(-Math.PI / 2 + bottomMostIndex * step) * h) / 2;

    const diffX = w - Math.abs(maxX - minX);
    const diffY = h - Math.abs(maxY - minY);
    const offsetX = w / 2 + minX - (w / 2 - maxX);
    const offsetY = h / 2 + minY - (h / 2 - maxY);

    const ratio = 1;
    const cx = (w - offsetX) / 2;
    const cy = (h - offsetY) / 2;
    const ox = (w + diffX) / 2;
    const oy = (h + diffY) / 2;
    const ix = (ox * ratio) / 2;
    const iy = (oy * ratio) / 2;

    const points = Array.from({ length: sides * 2 }).map((_, i) => {
      const theta = -Math.PI / 2 + i * step;
      return new Vec(cx + (i % 2 ? ix : ox) * Math.cos(theta), cy + (i % 2 ? iy : oy) * Math.sin(theta));
    });
    const pathData = points.map((p, i) => (i === 0 ? "M" : "L") + ` ${p.x} ${p.y}`).join(" ") + " Z";

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <path
        d={pathData}
        transform={`translate(${isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0}, ${
          isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0
        })`}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}
